import { useState } from 'react';
import { CheckCircle, XCircle, FileText, Calendar, AlertCircle } from 'lucide-react';

interface FormVersion {
  id: string;
  form_code: string;
  version_number: string;
  file_url: string;
  file_type: string;
  effective_date: string;
  expiration_date: string | null;
  applicable_states: string[];
  change_notes: string;
  regulatory_ref: string | null;
  uploaded_at: string;
  uploaded_by: string;
}

interface ApprovalCardProps {
  version: FormVersion;
  formName: string;
  onApprove: (versionId: string, comments: string) => Promise<void>;
  onReject: (versionId: string, reason: string) => Promise<void>;
}

export default function ApprovalCard({
  version,
  formName,
  onApprove,
  onReject,
}: ApprovalCardProps) {
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await onApprove(version.id, comments);
      setShowApproveModal(false);
      setComments('');
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    
    setProcessing(true);
    try {
      await onReject(version.id, comments);
      setShowRejectModal(false);
      setComments('');
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-gray-400" />
              <h3 className="font-semibold text-lg text-gray-900">
                {version.form_code} v{version.version_number}
              </h3>
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                Pending Review
              </span>
            </div>
            <p className="text-gray-600">{formName}</p>
          </div>
          <a
            href={version.file_url}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Preview File
          </a>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Effective:</span>
            <span className="font-medium">
              {new Date(version.effective_date).toLocaleDateString()}
            </span>
          </div>
          {version.expiration_date && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Expires:</span>
              <span className="font-medium">
                {new Date(version.expiration_date).toLocaleDateString()}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-gray-600">File Type:</span>
            <span className="font-medium uppercase">{version.file_type}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">Uploaded:</span>
            <span className="font-medium">
              {new Date(version.uploaded_at).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Applicable States */}
        {version.applicable_states && version.applicable_states.length > 0 && (
          <div className="mb-4">
            <span className="text-sm text-gray-600">Applicable States:</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {version.applicable_states.map((state) => (
                <span
                  key={state}
                  className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                >
                  {state}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Change Notes */}
        {version.change_notes && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-1">Changes from Previous Version:</p>
            <p className="text-sm text-gray-600">{version.change_notes}</p>
          </div>
        )}

        {/* Regulatory Reference */}
        {version.regulatory_ref && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-900 mb-1">Regulatory Reference:</p>
            <p className="text-sm text-blue-800">{version.regulatory_ref}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowApproveModal(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Approve
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            <XCircle className="w-4 h-4" />
            Reject
          </button>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowApproveModal(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Approve Form Version
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to approve {version.form_code} v{version.version_number}?
                This will make it available for use.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (Optional)
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="Add any notes about this approval..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {processing ? 'Approving...' : 'Confirm Approval'}
                </button>
                <button
                  onClick={() => setShowApproveModal(false)}
                  disabled={processing}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowRejectModal(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Reject Form Version
                </h3>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Please provide a reason for rejecting {version.form_code} v{version.version_number}.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection *
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                  required
                  placeholder="Explain why this version is being rejected..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleReject}
                  disabled={processing || !comments.trim()}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {processing ? 'Rejecting...' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => setShowRejectModal(false)}
                  disabled={processing}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
